import GoogleMapReact from 'google-map-react';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { generateBounds } from './util/bounds';
import { DEFAULT_MAP_CENTER_POSITION, GEOFENCE_ACTION } from './util/constant';

export function MapGeofence({
  actionName,
  geofenceBounds,
  onSave,
  renderMarker,
  mapCenterLocation,
  mapOptions
}) {
  const { i18n } = useTranslation();
  const mapInstance = useRef();
  const mapApi = useRef();
  const geofenceInstance = useRef();
  const oldBoundsRef = useRef();

  const createGeofence = (action, bounds) => {
    const maps = mapApi.current;
    const map = mapInstance.current;
    const canEdit = action !== GEOFENCE_ACTION.INITIAL_GEOFENCE;

    geofenceInstance.current = new maps.Rectangle({
      bounds,
      strokeColor: 'yellow',
      fillColor: 'yellow',
      fillOpacity: 0.3,
      strokeWeight: 10,
      editable: canEdit,
      draggable: canEdit
    });

    const rectangle = geofenceInstance.current;
    rectangle.setMap(map);
    oldBoundsRef.current = bounds;
  };

  const switchToEditableMode = () => {
    geofenceInstance.current.setOptions({
      editable: true,
      draggable: true,
      fillOpacity: 0
    });
  };

  const discardChanges = () => {
    geofenceInstance.current.setOptions({
      editable: false,
      draggable: false,
      bounds: oldBoundsRef.current,
      fillOpacity: 0.3
    });
  };

  const saveChanges = useCallback(() => {
    geofenceInstance.current.setOptions({
      editable: false,
      draggable: false,
      fillOpacity: 0.3
    });

    const rectangleBounds = geofenceInstance.current.getBounds();

    const newBounds = {
      north: rectangleBounds.getNorthEast().lat(),
      east: rectangleBounds.getNorthEast().lng(),
      south: rectangleBounds.getSouthWest().lat(),
      west: rectangleBounds.getSouthWest().lng()
    };

    oldBoundsRef.current = newBounds;
    onSave(newBounds);
  }, [onSave]);

  const executeAction = useCallback(
    (action) => {
      switch (action) {
        case GEOFENCE_ACTION.EDIT: {
          // Case 1: Has geofence
          // Case 2: Hasn't geofence
          if (!geofenceInstance.current) {
            createGeofence(
              GEOFENCE_ACTION.INITIAL_GEOFENCE,
              generateBounds(mapCenterLocation)
            );
          }

          switchToEditableMode();
          break;
        }

        case GEOFENCE_ACTION.DISCARD: {
          discardChanges();
          break;
        }

        case GEOFENCE_ACTION.SAVE: {
          saveChanges();
          break;
        }

        case GEOFENCE_ACTION.INITIAL_GEOFENCE: {
          if (!oldBoundsRef.current) {
            createGeofence(action, geofenceBounds);
          }
          break;
        }

        default:
          break;
      }
    },
    [geofenceBounds, mapCenterLocation, saveChanges]
  );

  useEffect(() => {
    if (actionName) {
      executeAction(actionName);
    }
  }, [actionName, executeAction]);

  const handleGoogleApiLoaded = useCallback(({ map, maps }) => {
    mapInstance.current = map;
    mapApi.current = maps;
  }, []);

  return (
    <GoogleMapReact
      bootstrapURLKeys={{
        key: process.env.REACT_APP_GOOGLE_API,
        language: i18n.language
      }}
      yesIWantToUseGoogleMapApiInternals
      onGoogleApiLoaded={handleGoogleApiLoaded}
      center={mapCenterLocation}
      {...mapOptions}>
      {renderMarker()}
    </GoogleMapReact>
  );
}

MapGeofence.defaultProps = {
  mapOptions: {
    zoom: 6
  },
  mapCenterLocation: [
    DEFAULT_MAP_CENTER_POSITION.LAT,
    DEFAULT_MAP_CENTER_POSITION.LNG
  ],
  geofenceBounds: null,
  renderMarker: () => null
};

MapGeofence.propTypes = {
  mapOptions: PropTypes.shape({
    zoom: PropTypes.number
  }),
  mapCenterLocation: PropTypes.arrayOf(PropTypes.number),
  onSave: PropTypes.func.isRequired,
  actionName: PropTypes.string.isRequired,
  renderMarker: PropTypes.func,
  geofenceBounds: PropTypes.shape({
    north: PropTypes.number.isRequired,
    east: PropTypes.number.isRequired,
    south: PropTypes.number.isRequired,
    west: PropTypes.number.isRequired
  })
};
